import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Briefcase,
  CheckCircle,
  Loader2,
  User,
  Plane,
  Luggage,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Calendar,
  Globe,
  CreditCard,
  ArrowRight,
  UserPlus,
  Mail,
  Phone,
  Lock,
  Plus,
  Trash2,
  AlertCircle,
} from "lucide-react";
import PrimaryButton from "../../components/common/buttons/PrimaryButton";
import Toast from "../../components/common/toast/Toast";
import {
  previewTerminalBooking,
  saveTerminalBooking,
  ConfirmBookingPayload,
  BookingPassengerDetail,
  ManualFlightSegment,
} from "../../service/terminalbookings/terminalBookingService";
import { getAllUsersFromDB } from "../../service/dashboard/dashBoardServicel";
import { User as UserType } from "../../types/types";
import countriesData from "../../data/countries.json";
import airportsData from "../../data/airportss.json";

// ✅ Added Phone Input Library
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const API_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

const adminCreateUser = async (userData: any) => {
  try {
    const payload = { ...userData };

    // ✅ FIX: If email is empty, delete it from payload entirely so it isn't sent as ""
    if (!payload.email || payload.email.trim() === "") {
      delete payload.email;
    }

    // ✅ Ensure phone number has a '+' for proper international formatting in DB
    if (payload.contact && !payload.contact.startsWith("+")) {
      payload.contact = `+${payload.contact}`;
    }

    const response = await axios.post(
      `${API_URL}/user-auth/admin-create`,
      payload,
    );
    return { success: true, data: response.data };
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || "User creation failed",
    };
  }
};

// --- REUSABLE CARD COMPONENTS ---
const Card: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  children,
  className = "",
}) => (
  <div className={`bg-white shadow-md rounded-lg overflow-hidden ${className}`}>
    {children}
  </div>
);

const CardHeader: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  children,
  className = "",
}) => (
  <div className={`px-6 py-4 bg-gray-50 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardContent: React.FC<
  React.PropsWithChildren<{ className?: string }>
> = ({ children, className = "" }) => (
  <div className={`px-6 py-4 ${className}`}>{children}</div>
);

// --- TYPES ---
interface ToastState {
  show: boolean;
  message: string;
  type: "success" | "error" | "warning";
}

interface PassengerInput extends BookingPassengerDetail {}

const TerminalBookingImport: React.FC = () => {
  // --- STATE ---
  const [step, setStep] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [toastState, setToastState] = useState<ToastState>({
    show: false,
    message: "",
    type: "success",
  });

  // Step 1: Search & User Data
  const [pnr, setPnr] = useState("");
  const [userMode, setUserMode] = useState<"existing" | "new">("existing");
  const [selectedExistingEmail, setSelectedExistingEmail] = useState("");
  const [usersList, setUsersList] = useState<UserType[]>([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);

  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  // New User Form State
  const [newUserForm, setNewUserForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    contact: "",
    password: "",
    confirmPassword: "",
    gender: "Male",
  });

  // --- NDC / MANUAL ENTRY STATE ---
  const [isNDC, setIsNDC] = useState(false);
  const [manualFlights, setManualFlights] = useState<ManualFlightSegment[]>([]);
  const [manualPaxCounts, setManualPaxCounts] = useState({
    adult: 1,
    child: 0,
    infant: 0,
  });

  const [newSegment, setNewSegment] = useState<ManualFlightSegment>({
    airlineCode: "",
    airlineName: "",
    flightNumber: "",
    rbd: "",
    travelClass: "Y",
    origin: "",
    originCity: "",
    originCountry: "",
    originAirport: "",
    destination: "",
    destinationCity: "",
    destinationCountry: "",
    destinationAirport: "",
    departureDateTime: "",
    arrivalDateTime: "",
    type: "ob",
  });

  // Step 2 & 3: Booking Data
  const [previewData, setPreviewData] = useState<any>(null);
  const [passengerInputs, setPassengerInputs] = useState<PassengerInput[]>([]);
  const [expandedPassengerIndex, setExpandedPassengerIndex] = useState<
    number | null
  >(0);
  const [currency, setCurrency] = useState("EUR");

  // Step 3: Global Booking & Payment Settings
  const [bookingStatus, setBookingStatus] = useState<
    "Completed" | "Pending" | "Cancelled"
  >("Pending");
  const [travelClass, setTravelClass] = useState("Y");

  // Baggage (Global Config)
  const [baggageConfig, setBaggageConfig] = useState({
    isKg: true,
    outbound: { ADT: "30", CHD: "20", INF: "10" },
    inbound: { ADT: "30", CHD: "20", INF: "10" },
  });

  // New Payment Fields
  const [paidAt, setPaidAt] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [paymentType, setPaymentType] = useState<string>("");
  const [platform, setPlatform] = useState("IATA");
  const [issuedFrom, setIssuedFrom] = useState("IATA");
  const [agentCategory, setAgentCategory] = useState("Findyourfares");
  const [ticketCost, setTicketCost] = useState("");

  // Step 4: Success Data
  const [savedReference, setSavedReference] = useState("");

  // --- EFFECTS ---
  useEffect(() => {
    const fetchUsers = async () => {
      setIsUsersLoading(true);
      try {
        const data = await getAllUsersFromDB();
        if (data && Array.isArray(data)) {
          setUsersList(data);
        }
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setIsUsersLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // --- HANDLERS ---

  const handleCloseToast = () => setToastState({ ...toastState, show: false });
  const handleNewUserChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setNewUserForm({ ...newUserForm, [e.target.name]: e.target.value });
  };

  const handleBaggageChange = (
    journey: "outbound" | "inbound",
    type: "ADT" | "CHD" | "INF",
    value: string,
  ) => {
    setBaggageConfig((prev) => ({
      ...prev,
      [journey]: { ...prev[journey], [type]: value },
    }));
  };

  const hasReturnFlight = isNDC
    ? manualFlights.some((f) => f.type === "ib")
    : previewData?.segments?.some((s: any) => s.type === "ib") || false;

  const activeTypes = new Set(passengerInputs.map((p) => p.type));

  // --- STEP 1 LOGIC: PROCEED TO FETCH OR MANUAL ENTRY ---
  const handlePreview = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pnr) {
      setToastState({
        show: true,
        message: "PNR Number is required",
        type: "error",
      });
      return;
    }

    let emailToUse = "";

    if (userMode === "existing") {
      if (!selectedExistingEmail) {
        setToastState({
          show: true,
          message: "Please select an existing user from the dropdown",
          type: "error",
        });
        return;
      }
      emailToUse = selectedExistingEmail;
    } else {
      const { email, firstName, lastName, password, confirmPassword, contact } =
        newUserForm;

      if (!firstName || !lastName || !password) {
        setToastState({
          show: true,
          message: "Please fill in the Name and Password fields",
          type: "error",
        });
        return;
      }

      // ✅ Require EITHER email OR contact
      if (!email && !contact) {
        setToastState({
          show: true,
          message: "Please provide either an Email Address or a Contact Number",
          type: "error",
        });
        return;
      }

      if (password !== confirmPassword) {
        setToastState({
          show: true,
          message: "Passwords do not match",
          type: "error",
        });
        return;
      }

      setLoading(true);
      const createRes = await adminCreateUser(newUserForm);

      if (!createRes.success) {
        setLoading(false);
        setToastState({
          show: true,
          message: createRes.message || "Failed to create new user",
          type: "error",
        });
        return;
      }

      // ✅ Use email if available, otherwise safely fallback to formatted contact
      const formattedContact = contact.startsWith("+")
        ? contact
        : `+${contact}`;
      emailToUse = email || formattedContact;

      setSelectedExistingEmail(emailToUse);
      setToastState({
        show: true,
        message: "User created successfully!",
        type: "success",
      });
    }

    if (isNDC) {
      setLoading(false);
      setStep(1.5);
    } else {
      fetchAmadeusBooking(pnr);
    }
  };

  const fetchAmadeusBooking = async (pnrVal: string) => {
    setLoading(true);
    try {
      const res = await previewTerminalBooking(pnrVal);
      if (res.success && res.data) {
        setPreviewData(res.data);

        const initialInputs: PassengerInput[] = res.data.travellers.map(
          (t: any) => ({
            type: t.type,
            firstName: t.firstName,
            lastName: t.lastName,
            title:
              t.type === "ADT" ? "Mr" : t.type === "CHD" ? "Master" : "Miss",
            gender: "Male",
            dateOfBirth: "",
            nationality: "",
            passportNumber: "",
            passportIssuingCountry: "",
            passportIssueDate: "",
            passportExpiry: "",
            price: "",
            tax: "",
          }),
        );
        setPassengerInputs(initialInputs);

        if (res.data.segments?.length > 0) {
          const origin = res.data.segments[0].origin;
          setCurrency(origin === "CMB" ? "LKR" : "EUR");

          if (res.data.travelClass) {
            const incoming = res.data.travelClass.toUpperCase();
            if (["Y", "W", "C", "F"].includes(incoming)) {
              setTravelClass(incoming);
            } else {
              setTravelClass("Y");
            }
          }
        }
        setStep(2);
      }
    } catch (err: any) {
      setToastState({
        show: true,
        message: err.message || "Failed to fetch PNR",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // --- MANUAL FLIGHT LOGIC ---
  const handleCodeChange = (field: "origin" | "destination", value: string) => {
    const code = value.toUpperCase();
    const airport = (airportsData as any[]).find((a) => a.code === code);

    setNewSegment((prev) => ({
      ...prev,
      [field]: code,
      ...(airport &&
        field === "origin" && {
          originAirport: airport.name,
          originCity: airport.city,
          originCountry: airport.country,
        }),
      ...(airport &&
        field === "destination" && {
          destinationAirport: airport.name,
          destinationCity: airport.city,
          destinationCountry: airport.country,
        }),
    }));
  };

  const addManualSegment = () => {
    const {
      airlineCode,
      flightNumber,
      origin,
      destination,
      departureDateTime,
      arrivalDateTime,
      rbd,
    } = newSegment;

    if (
      !airlineCode ||
      !flightNumber ||
      !origin ||
      !destination ||
      !departureDateTime ||
      !arrivalDateTime ||
      !rbd
    ) {
      setToastState({
        show: true,
        message: "Please fill in all mandatory flight fields (*)",
        type: "error",
      });
      return;
    }

    setManualFlights([...manualFlights, newSegment]);
    setNewSegment({
      ...newSegment,
      origin: destination,
      originCity: newSegment.destinationCity,
      originCountry: newSegment.destinationCountry,
      originAirport: newSegment.destinationAirport,
      destination: "",
      destinationCity: "",
      destinationCountry: "",
      destinationAirport: "",
      flightNumber: "",
      departureDateTime: "",
      arrivalDateTime: "",
      rbd: "",
    });
  };

  const removeManualSegment = (index: number) => {
    const updated = [...manualFlights];
    updated.splice(index, 1);
    setManualFlights(updated);
  };

  const initializeManualPassengers = () => {
    if (manualFlights.length === 0) {
      setToastState({
        show: true,
        message: "Please add at least one flight segment.",
        type: "error",
      });
      return;
    }

    const { adult, child, infant } = manualPaxCounts;
    if (adult + child + infant === 0) {
      setToastState({
        show: true,
        message: "Please add at least one passenger.",
        type: "error",
      });
      return;
    }

    const newInputs: PassengerInput[] = [];
    const createEmpty = (type: string): PassengerInput => ({
      type,
      firstName: "",
      lastName: "",
      title: type === "ADT" ? "Mr" : type === "CHD" ? "Master" : "Miss",
      gender: "Male",
      dateOfBirth: "",
      nationality: "",
      passportNumber: "",
      passportIssuingCountry: "",
      passportIssueDate: "",
      passportExpiry: "",
      price: "",
      tax: "",
    });

    for (let i = 0; i < adult; i++) newInputs.push(createEmpty("ADT"));
    for (let i = 0; i < child; i++) newInputs.push(createEmpty("CHD"));
    for (let i = 0; i < infant; i++) newInputs.push(createEmpty("INF"));

    setPassengerInputs(newInputs);

    const firstOrigin = manualFlights[0].origin;
    setCurrency(firstOrigin === "CMB" ? "LKR" : "EUR");
    setTravelClass(manualFlights[0].travelClass || "Y");

    setStep(3);
  };

  // --- STEP 3 LOGIC: CONFIRM & SAVE ---
  const handleInputChange = (
    index: number,
    field: keyof PassengerInput,
    value: any,
  ) => {
    const newInputs = [...passengerInputs];
    // @ts-ignore
    newInputs[index] = { ...newInputs[index], [field]: value };
    setPassengerInputs(newInputs);
  };

  const togglePassengerExpand = (index: number) => {
    setExpandedPassengerIndex(expandedPassengerIndex === index ? null : index);
  };

  const calculateTotals = () => {
    const totalFare = passengerInputs.reduce(
      (acc, curr) => acc + (parseFloat(curr.price) || 0),
      0,
    );
    const totalTax = passengerInputs.reduce(
      (acc, curr) => acc + (parseFloat(curr.tax) || 0),
      0,
    );
    return { totalFare, totalTax, grandTotal: totalFare + totalTax };
  };

  const handleSave = async () => {
    if (isNDC && (!platform || !ticketCost || !issuedFrom || !agentCategory)) {
      setToastState({
        show: true,
        message:
          "Please fill in all NDC fields (Agent, Platform, Issued From, Category, Cost).",
        type: "error",
      });
      return;
    }

    const incompletePassengers = passengerInputs.some((p) => {
      const isInvalid =
        p.price === "" ||
        p.tax === "" ||
        !p.dateOfBirth ||
        !p.passportNumber ||
        !p.nationality ||
        !p.passportExpiry ||
        !p.title;
      return isInvalid;
    });

    if (incompletePassengers) {
      setToastState({
        show: true,
        message: "Please fill in all Pricing, Passport, and Title details.",
        type: "error",
      });
      return;
    }

    const activeTypes = new Set(passengerInputs.map((p) => p.type));
    const isValidBaggage = (val: string) =>
      val !== "" && val !== null && val !== undefined;

    const missingOutbound =
      (activeTypes.has("ADT") && !isValidBaggage(baggageConfig.outbound.ADT)) ||
      (activeTypes.has("CHD") && !isValidBaggage(baggageConfig.outbound.CHD)) ||
      (activeTypes.has("INF") && !isValidBaggage(baggageConfig.outbound.INF));

    const missingInbound =
      hasReturnFlight &&
      ((activeTypes.has("ADT") && !isValidBaggage(baggageConfig.inbound.ADT)) ||
        (activeTypes.has("CHD") &&
          !isValidBaggage(baggageConfig.inbound.CHD)) ||
        (activeTypes.has("INF") && !isValidBaggage(baggageConfig.inbound.INF)));

    if (missingOutbound || missingInbound) {
      setToastState({
        show: true,
        message: "Please fill in Baggage Allowance for all passenger types.",
        type: "error",
      });
      return;
    }

    if (bookingStatus === "Completed" && (!paidAt || !paymentType)) {
      setToastState({
        show: true,
        message: "Please select Payment details for completed bookings.",
        type: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const totals = calculateTotals();

      let finalEmail = selectedExistingEmail;

      if (userMode === "new") {
        const finalContact = newUserForm.contact.startsWith("+")
          ? newUserForm.contact
          : `+${newUserForm.contact}`;
        finalEmail = newUserForm.email || finalContact;
      }

      const payload: ConfirmBookingPayload = {
        controlNumber: pnr,
        email: finalEmail,
        isGuest: false,
        passengerDetails: passengerInputs,
        baggageDetails: baggageConfig,
        totalPrice: totals.grandTotal,
        totalTax: totals.totalTax,
        currency: currency,
        bookingStatus: bookingStatus,
        travelClass: travelClass,
        paidAt: bookingStatus === "Completed" ? paidAt : undefined,
        paymentType: bookingStatus === "Completed" ? paymentType : undefined,
        isNDC: isNDC,
        manualFlights: isNDC ? manualFlights : undefined,
        agent: isNDC ? agentCategory : undefined,
        platform: isNDC ? platform : undefined,
        ticketCost: isNDC ? parseFloat(ticketCost) : undefined,
        issuedFrom: isNDC ? issuedFrom : undefined,
        agentCategory: isNDC ? agentCategory : undefined,
      };

      const res = await saveTerminalBooking(payload);
      if (res.success) {
        setSavedReference(res.arReference || "Saved");
        setStep(4);
        setToastState({
          show: true,
          message: "Booking imported successfully!",
          type: "success",
        });
      }
    } catch (err: any) {
      console.error("Save Error:", err);
      setToastState({
        show: true,
        message: err.message || "Failed to save booking.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setPnr("");
    setUserMode("existing");
    setIsNDC(false);
    setManualFlights([]);
    setManualPaxCounts({ adult: 1, child: 0, infant: 0 });
    setNewUserForm({
      email: "",
      firstName: "",
      lastName: "",
      contact: "",
      password: "",
      confirmPassword: "",
      gender: "Male",
    });
    setSelectedExistingEmail("");
    setPreviewData(null);
    setPassengerInputs([]);
    setExpandedPassengerIndex(0);
    setBookingStatus("Pending");
    setPaymentType("");
    setPaidAt(new Date().toISOString().split("T")[0]);
    setPlatform("");
    setTicketCost("");
  };

  const { totalFare, totalTax, grandTotal } = calculateTotals();

  // Styles
  const inputStyle =
    "w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm";
  const labelStyle =
    "block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wide";

  // Filter users based on search query
  const filteredUsers = usersList.filter((u) => {
    const searchStr = userSearchQuery.toLowerCase();
    const name = `${u.firstName} ${u.lastName}`.toLowerCase();
    const emailStr = (u.email || "").toLowerCase();
    const contactStr = (u.contact || "").toLowerCase();
    return (
      name.includes(searchStr) ||
      emailStr.includes(searchStr) ||
      contactStr.includes(searchStr)
    );
  });

  // Get the currently selected user object for display purposes
  const selectedUserDisplay = usersList.find(
    (u) => (u.email || u.contact) === selectedExistingEmail,
  );

  return (
    <div className="container mx-auto p-4 pt-10 max-w-6xl">
      <h2 className="text-3xl font-bold mb-8 text-gray-800 flex items-center">
        <Briefcase size={28} className="inline mr-3 text-blue-600" />
        Terminal Booking Import
      </h2>

      {toastState.show && (
        <Toast
          type={toastState.type}
          message={toastState.message}
          onClose={handleCloseToast}
        />
      )}

      {/* --- STEP 1: INITIAL SEARCH & USER --- */}
      {step === 1 && (
        <div className="bg-white p-8 shadow-xl rounded-xl border border-gray-200">
          <h3 className="font-semibold text-lg mb-6 text-blue-600 border-b pb-2">
            Step 1: Booking & User Selection
          </h3>
          <form onSubmit={handlePreview} className="space-y-6">
            <div className="flex bg-gray-100 p-1 rounded-lg w-fit">
              <button
                type="button"
                onClick={() => setUserMode("existing")}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                  userMode === "existing"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Existing User
              </button>
              <button
                type="button"
                onClick={() => setUserMode("new")}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center ${
                  userMode === "new"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <UserPlus size={16} className="mr-1" /> Create New User
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PNR Number
                </label>
                <input
                  type="text"
                  value={pnr}
                  onChange={(e) => setPnr(e.target.value.toUpperCase())}
                  className="w-full p-3 border border-gray-300 rounded-lg uppercase font-mono focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="e.g. X9LAVY"
                  maxLength={6}
                />

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center">
                  <input
                    id="ndc-check"
                    type="checkbox"
                    checked={isNDC}
                    onChange={(e) => setIsNDC(e.target.checked)}
                    className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <label
                    htmlFor="ndc-check"
                    className="ml-3 text-sm font-bold text-gray-800 cursor-pointer"
                  >
                    This is an NDC (Manual) Booking
                  </label>
                </div>
                {isNDC && (
                  <p className="text-xs text-gray-500 mt-1 ml-1">
                    * NDC bookings require you to manually enter flight details
                    instead of fetching from Amadeus.
                  </p>
                )}
              </div>

              <div>
                {userMode === "existing" ? (
                  <div className="animate-fade-in">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Existing User
                    </label>
                    {isUsersLoading ? (
                      <div className="text-sm text-gray-500 flex items-center">
                        <Loader2 className="animate-spin w-4 h-4 mr-2" />{" "}
                        Loading users...
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search by name, email, or phone..."
                          value={
                            isUserDropdownOpen
                              ? userSearchQuery
                              : selectedUserDisplay
                                ? `${selectedUserDisplay.firstName} ${selectedUserDisplay.lastName} (${selectedUserDisplay.email || selectedUserDisplay.contact})`
                                : ""
                          }
                          onFocus={() => {
                            setIsUserDropdownOpen(true);
                            setUserSearchQuery(""); // Clear search on open to show all users
                          }}
                          onBlur={() =>
                            setTimeout(() => setIsUserDropdownOpen(false), 200)
                          } // Slight delay to allow clicking list items
                          onChange={(e) => {
                            setUserSearchQuery(e.target.value);
                            setIsUserDropdownOpen(true);
                          }}
                          className="w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                        />

                        {isUserDropdownOpen && (
                          <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-auto">
                            {filteredUsers.length > 0 ? (
                              filteredUsers.map((u) => {
                                const identifier = u.email || u.contact;
                                return (
                                  <li
                                    key={u.id}
                                    className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-none transition-colors"
                                    onMouseDown={(e) => {
                                      // Using onMouseDown instead of onClick so it fires before onBlur
                                      e.preventDefault();
                                      setSelectedExistingEmail(identifier);
                                      setIsUserDropdownOpen(false);
                                      setUserSearchQuery("");
                                    }}
                                  >
                                    <div className="font-bold text-gray-800">
                                      {u.firstName} {u.lastName}
                                    </div>
                                    <div className="text-sm text-gray-500 flex items-center space-x-2">
                                      {u.email && <span>{u.email}</span>}
                                      {u.email && u.contact && <span className="text-gray-300">|</span>}
                                      {u.contact && <span>{u.contact}</span>}
                                    </div>
                                  </li>
                                );
                              })
                            ) : (
                              <li className="p-4 text-sm text-gray-500 text-center italic">
                                No users found
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 space-y-4 animate-fade-in">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-bold text-blue-700">
                        New User Registration
                      </h4>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-600 font-medium">
                          First Name *
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={newUserForm.firstName}
                          onChange={handleNewUserChange}
                          className={inputStyle}
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 font-medium">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={newUserForm.lastName}
                          onChange={handleNewUserChange}
                          className={inputStyle}
                          placeholder="Doe"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 font-medium">
                        Email{" "}
                        <span className="font-normal italic text-gray-400">
                          (Optional if Contact provided)
                        </span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={newUserForm.email}
                        onChange={handleNewUserChange}
                        className={inputStyle}
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 font-medium">
                        Contact Number{" "}
                        <span className="font-normal italic text-gray-400">
                          (Optional if Email provided)
                        </span>
                      </label>
                      {/* ✅ ADDED: react-phone-input-2 for better international support */}
                      <PhoneInput
                        country={"lk"}
                        value={newUserForm.contact}
                        onChange={(val) =>
                          setNewUserForm({ ...newUserForm, contact: val })
                        }
                        inputClass="!w-full !h-[38px] !text-sm !pl-12 !border-gray-300 !rounded-md focus:!ring-2 focus:!ring-blue-500"
                        containerClass="!w-full"
                        buttonClass="!border-gray-300 !rounded-l-md !bg-gray-50"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-600 font-medium">
                          Password *
                        </label>
                        <input
                          type="password"
                          name="password"
                          value={newUserForm.password}
                          onChange={handleNewUserChange}
                          className={inputStyle}
                          placeholder="******"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 font-medium">
                          Confirm *
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={newUserForm.confirmPassword}
                          onChange={handleNewUserChange}
                          className={inputStyle}
                          placeholder="******"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <PrimaryButton
              type="submit"
              disabled={loading}
              className="w-full py-3 justify-center mt-4"
            >
              {loading ? (
                <Loader2 className="animate-spin" />
              ) : isNDC ? (
                "Proceed to Manual Flight Entry"
              ) : (
                "Fetch Booking Details"
              )}
            </PrimaryButton>
          </form>
        </div>
      )}

      {/* --- STEP 1.5: MANUAL FLIGHT ENTRY (NDC ONLY) --- */}
      {step === 1.5 && isNDC && (
        <div className="bg-white p-8 shadow-xl rounded-xl border border-gray-200">
          <h3 className="font-semibold text-lg mb-6 text-blue-600 border-b pb-2 flex items-center">
            <Plane className="mr-2" size={20} /> Step 2: Enter Flight Segments
            (Manual)
          </h3>

          <div className="mb-8 space-y-3">
            {manualFlights.length === 0 && (
              <p className="text-gray-500 italic text-sm text-center py-4 bg-gray-50 rounded border border-dashed">
                No flight segments added yet. Use the form below.
              </p>
            )}
            {manualFlights.map((flight, idx) => (
              <div
                key={idx}
                className="flex justify-between items-center bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded text-xs mr-2">
                      {flight.type === "ob" ? "OUTBOUND" : "INBOUND"}
                    </span>
                    <span className="font-bold">
                      {flight.airlineCode} {flight.flightNumber}
                    </span>
                    <span className="mx-2 text-gray-400">|</span>
                    <span className="font-medium">
                      {flight.origin} ➔ {flight.destination}
                    </span>
                  </div>
                  <div className="text-gray-600">
                    Dep: {new Date(flight.departureDateTime).toLocaleString()}{" "}
                    <br />
                    Arr: {new Date(flight.arrivalDateTime).toLocaleString()}
                  </div>
                </div>
                <button
                  onClick={() => removeManualSegment(idx)}
                  className="ml-4 p-2 text-red-500 hover:bg-red-100 rounded-full transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 p-5 rounded-xl border border-blue-100">
            <h4 className="text-sm font-bold text-blue-800 mb-4 uppercase tracking-wide">
              Add New Segment
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className={labelStyle}>Journey Type</label>
                <select
                  className={inputStyle}
                  value={newSegment.type}
                  onChange={(e) =>
                    setNewSegment({
                      ...newSegment,
                      type: e.target.value as any,
                    })
                  }
                >
                  <option value="ob">Outbound</option>
                  <option value="ib">Inbound (Return)</option>
                </select>
              </div>
              <div>
                <label className={labelStyle}>Airline Code *</label>
                <input
                  className={inputStyle}
                  placeholder="e.g. EK"
                  value={newSegment.airlineCode}
                  onChange={(e) =>
                    setNewSegment({
                      ...newSegment,
                      airlineCode: e.target.value.toUpperCase(),
                    })
                  }
                />
              </div>
              <div>
                <label className={labelStyle}>Airline Name</label>
                <input
                  className={inputStyle}
                  placeholder="Emirates"
                  value={newSegment.airlineName}
                  onChange={(e) =>
                    setNewSegment({
                      ...newSegment,
                      airlineName: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className={labelStyle}>Flight No *</label>
                <input
                  className={inputStyle}
                  placeholder="e.g. 123"
                  value={newSegment.flightNumber}
                  onChange={(e) =>
                    setNewSegment({
                      ...newSegment,
                      flightNumber: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className={labelStyle}>RBD (Class Code) *</label>
                <input
                  className={inputStyle}
                  placeholder="e.g. Y, J, K"
                  value={newSegment.rbd}
                  onChange={(e) =>
                    setNewSegment({
                      ...newSegment,
                      rbd: e.target.value.toUpperCase(),
                    })
                  }
                />
              </div>
              <div>
                <label className={labelStyle}>Travel Class</label>
                <select
                  className={inputStyle}
                  value={newSegment.travelClass}
                  onChange={(e) =>
                    setNewSegment({
                      ...newSegment,
                      travelClass: e.target.value,
                    })
                  }
                >
                  <option value="Y">Economy</option>
                  <option value="W">Premium Economy</option>
                  <option value="C">Business</option>
                  <option value="F">First Class</option>
                </select>
              </div>
              <div className="md:col-span-2"></div>

              <div className="md:col-span-4 border-t border-blue-200 my-2"></div>
              <div>
                <label className={labelStyle}>Origin Code *</label>
                <input
                  className={inputStyle}
                  placeholder="DXB"
                  value={newSegment.origin}
                  onChange={(e) => handleCodeChange("origin", e.target.value)}
                />
              </div>
              <div>
                <label className={labelStyle}>Origin City</label>
                <input
                  className={inputStyle}
                  placeholder="Dubai"
                  value={newSegment.originCity}
                  onChange={(e) =>
                    setNewSegment({ ...newSegment, originCity: e.target.value })
                  }
                />
              </div>
              <div>
                <label className={labelStyle}>Origin Country</label>
                <input
                  className={inputStyle}
                  placeholder="UAE"
                  value={newSegment.originCountry}
                  onChange={(e) =>
                    setNewSegment({
                      ...newSegment,
                      originCountry: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className={labelStyle}>Origin Airport</label>
                <input
                  className={inputStyle}
                  placeholder="International..."
                  value={newSegment.originAirport}
                  onChange={(e) =>
                    setNewSegment({
                      ...newSegment,
                      originAirport: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className={labelStyle}>Dest. Code *</label>
                <input
                  className={inputStyle}
                  placeholder="LHR"
                  value={newSegment.destination}
                  onChange={(e) =>
                    handleCodeChange("destination", e.target.value)
                  }
                />
              </div>
              <div>
                <label className={labelStyle}>Dest. City</label>
                <input
                  className={inputStyle}
                  placeholder="London"
                  value={newSegment.destinationCity}
                  onChange={(e) =>
                    handleCodeChange("destination", e.target.value)
                  }
                />
              </div>
              <div>
                <label className={labelStyle}>Dest. Country</label>
                <input
                  className={inputStyle}
                  placeholder="UK"
                  value={newSegment.destinationCountry}
                  onChange={(e) =>
                    setNewSegment({
                      ...newSegment,
                      destinationCountry: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className={labelStyle}>Dest. Airport</label>
                <input
                  className={inputStyle}
                  placeholder="Heathrow..."
                  value={newSegment.destinationAirport}
                  onChange={(e) =>
                    setNewSegment({
                      ...newSegment,
                      destinationAirport: e.target.value,
                    })
                  }
                />
              </div>

              <div className="md:col-span-4 border-t border-blue-200 my-2"></div>
              <div>
                <label className={labelStyle}>Departure Time *</label>
                <input
                  type="datetime-local"
                  className={inputStyle}
                  value={newSegment.departureDateTime}
                  onChange={(e) =>
                    setNewSegment({
                      ...newSegment,
                      departureDateTime: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className={labelStyle}>Arrival Time *</label>
                <input
                  type="datetime-local"
                  className={inputStyle}
                  value={newSegment.arrivalDateTime}
                  onChange={(e) =>
                    setNewSegment({
                      ...newSegment,
                      arrivalDateTime: e.target.value,
                    })
                  }
                />
              </div>
              <div className="md:col-span-2 flex items-end">
                <button
                  type="button"
                  onClick={addManualSegment}
                  className="w-full bg-blue-600 text-white font-bold py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus size={18} /> Add Flight Segment
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="font-semibold text-lg text-gray-800 mb-4">
              Passenger Counts
            </h3>
            <div className="grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <label className={labelStyle}>Adults</label>
                <input
                  type="number"
                  min="1"
                  className={inputStyle}
                  value={manualPaxCounts.adult}
                  onChange={(e) =>
                    setManualPaxCounts({
                      ...manualPaxCounts,
                      adult: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div>
                <label className={labelStyle}>Children</label>
                <input
                  type="number"
                  min="0"
                  className={inputStyle}
                  value={manualPaxCounts.child}
                  onChange={(e) =>
                    setManualPaxCounts({
                      ...manualPaxCounts,
                      child: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div>
                <label className={labelStyle}>Infants</label>
                <input
                  type="number"
                  min="0"
                  className={inputStyle}
                  value={manualPaxCounts.infant}
                  onChange={(e) =>
                    setManualPaxCounts({
                      ...manualPaxCounts,
                      infant: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-8">
            <button
              onClick={() => setStep(1)}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              Back
            </button>
            <PrimaryButton
              onClick={initializeManualPassengers}
              className="px-8 py-2"
            >
              Continue to Passenger Details
            </PrimaryButton>
          </div>
        </div>
      )}

      {/* --- STEP 2: VERIFICATION (AMADEUS API ONLY) --- */}
      {step === 2 && previewData && !isNDC && (
        <div className="space-y-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-800">
              Verify Booking Details
            </h3>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              PNR: {previewData.controlNumber}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-indigo-600 flex items-center">
                  <User size={20} className="mr-2" /> Passengers (API)
                </h3>
              </CardHeader>
              <CardContent>
                {previewData.travellers.map((t: any, idx: number) => (
                  <div
                    key={idx}
                    className="mb-2 border-b pb-2 last:border-0 last:pb-0"
                  >
                    <p className="text-gray-800 font-medium">
                      {t.firstName} {t.lastName}
                    </p>
                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-500">
                      {t.type}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-indigo-600 flex items-center">
                  <Plane size={20} className="mr-2" /> Flights (API)
                </h3>
              </CardHeader>
              <CardContent>
                {previewData.segments.map((seg: any, idx: number) => (
                  <div
                    key={idx}
                    className="mb-4 last:mb-0 border-b last:border-0 pb-2"
                  >
                    <p className="font-bold text-gray-800">
                      {seg.airline} {seg.flightNumber}
                    </p>
                    <p className="text-sm text-gray-600 flex items-center">
                      {seg.origin} <ArrowRight size={14} className="mx-1" />{" "}
                      {seg.destination}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(seg.departureDateTime).toLocaleString()}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              onClick={() => setStep(1)}
              className="px-6 py-3 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              Back
            </button>
            <PrimaryButton
              onClick={() => setStep(3)}
              className="px-8 py-3 flex-1 justify-center text-lg"
            >
              Confirm & Proceed
            </PrimaryButton>
          </div>
        </div>
      )}

      {/* --- STEP 3: DETAILS ENTRY (COMMON) --- */}
      {step === 3 && (
        <div className="bg-white p-8 shadow-xl rounded-xl border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 border-b pb-4">
            <h3 className="font-semibold text-lg text-blue-600">
              Step 3: Complete Details
            </h3>
            <div className="flex items-center gap-2 mt-2 md:mt-0">
              <span className="text-xs font-bold text-gray-500 uppercase">
                Currency:
              </span>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="bg-blue-100 text-blue-800 px-2 py-1 rounded font-bold border-none outline-none cursor-pointer"
              >
                <option value="EUR">EUR</option>
                <option value="LKR">LKR</option>
              </select>
            </div>
          </div>

          {isNDC && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-bold text-yellow-800 mb-2 flex items-center">
                <AlertCircle size={16} className="mr-2" /> Manual Booking
                Summary
              </h4>
              <div className="space-y-1">
                {manualFlights.map((f, i) => (
                  <div key={i} className="text-sm text-yellow-900">
                    <span className="font-mono font-bold">
                      {f.airlineCode}
                      {f.flightNumber}
                    </span>{" "}
                    ({f.origin}-{f.destination})
                  </div>
                ))}
              </div>
            </div>
          )}
          {isNDC && (
            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h4 className="font-bold text-purple-800 mb-4 flex items-center">
                <Briefcase size={18} className="mr-2" /> NDC Booking Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className={labelStyle}>Agent*</label>
                  <select
                    className={inputStyle}
                    value={agentCategory}
                    onChange={(e) => setAgentCategory(e.target.value)}
                  >
                    <option value="Findyourfares">Findyourfares</option>
                    <option value="Upul">Upul</option>
                    <option value="Walk-in">Walk-in</option>
                  </select>
                </div>
                <div>
                  <label className={labelStyle}>Platform (Method) *</label>
                  <select
                    className={inputStyle}
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value)}
                  >
                    <option value="Amadeus">Amadeus</option>
                    <option value="IATA">IATA</option>
                    <option value="FLY DUBAI">FLY DUBAI</option>
                    <option value="MITALY">MITALY</option>
                    <option value="Tk Connect">Tk Connect</option>
                    <option value="EK Spark">EK Spark</option>
                  </select>
                </div>
                <div>
                  <label className={labelStyle}>
                    Ticket Cost (Buy Price) *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500 text-sm">
                      {currency}
                    </span>
                    <input
                      type="number"
                      className={`${inputStyle} pl-10`}
                      value={ticketCost}
                      onChange={(e) => setTicketCost(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className={labelStyle}>Issued From *</label>
                  <select
                    className={inputStyle}
                    value={issuedFrom}
                    onChange={(e) => setIssuedFrom(e.target.value)}
                  >
                    <option value="Originator">Originator</option>
                    <option value="IATA">IATA</option>
                    <option value="FLY DUBAI">FLY DUBAI</option>
                    <option value="MITALY">MITALY</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div>
              <label className={labelStyle}>Booking Status</label>
              <select
                value={bookingStatus}
                onChange={(e) => setBookingStatus(e.target.value as any)}
                className={inputStyle}
              >
                <option value="Pending">Pending</option>
                <option value="Completed">Completed (Paid)</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className={labelStyle}>Travel Class</label>
              <select
                value={travelClass}
                onChange={(e) => setTravelClass(e.target.value)}
                className={inputStyle}
              >
                <option value="Y">Economy</option>
                <option value="W">Premium Economy</option>
                <option value="C">Business</option>
                <option value="F">First Class</option>
              </select>
            </div>
          </div>

          {bookingStatus === "Completed" && (
            <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg animate-fade-in">
              <h4 className="font-bold text-green-800 mb-4 flex items-center">
                <CreditCard size={18} className="mr-2" /> Payment Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelStyle}>Paid At (Date)</label>
                  <input
                    type="date"
                    value={paidAt}
                    onChange={(e) => setPaidAt(e.target.value)}
                    className={`${inputStyle} border-green-300 focus:ring-green-500`}
                  />
                </div>
                <div>
                  <label className={labelStyle}>Payment Method</label>
                  <select
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                    className={`${inputStyle} border-green-300 focus:ring-green-500`}
                  >
                    <option value="">Select Payment Type...</option>
                    <option value="Stripe">Stripe</option>
                    <option value="Credit/Debit">Credit / Debit Card</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Revolut">Revolut</option>
                    <option value="Cash">Cash / Office</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <h4 className="font-bold text-gray-700 mb-4 flex items-center">
            <User size={18} className="mr-2" /> Passenger Information
          </h4>

          <div className="space-y-4">
            {passengerInputs.map((p, idx) => {
              const isExpanded = expandedPassengerIndex === idx;
              return (
                <div
                  key={idx}
                  className="border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm transition-all"
                >
                  <div
                    className={`p-4 flex justify-between items-center cursor-pointer ${
                      isExpanded
                        ? "bg-blue-50 border-b border-blue-100"
                        : "bg-gray-50 hover:bg-gray-100"
                    }`}
                    onClick={() => togglePassengerExpand(idx)}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          isExpanded
                            ? "bg-blue-500 text-white"
                            : "bg-gray-300 text-gray-600"
                        }`}
                      >
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-bold text-gray-800 text-sm">
                          {p.title ? `${p.title} ` : ""}
                          {p.firstName || `Passenger ${idx + 1}`}{" "}
                          {p.lastName || ""}
                        </p>
                        <p className="text-xs text-gray-500 uppercase">
                          {p.type}
                        </p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp size={20} className="text-blue-500" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>

                  {isExpanded && (
                    <div className="p-6 space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className={labelStyle}>Title *</label>
                          <select
                            className={inputStyle}
                            value={p.title}
                            onChange={(e) =>
                              handleInputChange(idx, "title", e.target.value)
                            }
                            required
                          >
                            <option value="">Select Title</option>
                            <option value="Mr">Mr</option>
                            <option value="Ms">Ms</option>
                            <option value="Mrs">Mrs</option>
                            <option value="Miss">Miss</option>
                            <option value="Master">Master</option>
                            <option value="Dr">Dr</option>
                          </select>
                        </div>
                        <div>
                          <label className={labelStyle}>First Name</label>
                          <input
                            type="text"
                            className={inputStyle}
                            value={p.firstName}
                            onChange={(e) =>
                              handleInputChange(
                                idx,
                                "firstName",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className={labelStyle}>Last Name</label>
                          <input
                            type="text"
                            className={inputStyle}
                            value={p.lastName}
                            onChange={(e) =>
                              handleInputChange(idx, "lastName", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <label className={labelStyle}>Gender</label>
                          <select
                            className={inputStyle}
                            value={p.gender}
                            onChange={(e) =>
                              handleInputChange(idx, "gender", e.target.value)
                            }
                          >
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className={labelStyle}>Date of Birth</label>
                          <input
                            type="date"
                            className={inputStyle}
                            value={p.dateOfBirth}
                            onChange={(e) =>
                              handleInputChange(
                                idx,
                                "dateOfBirth",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className={labelStyle}>Nationality</label>
                          <select
                            className={inputStyle}
                            value={p.nationality}
                            onChange={(e) =>
                              handleInputChange(
                                idx,
                                "nationality",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Select Nationality</option>
                            {countriesData.map((c: any) => (
                              <option key={c.code} value={c.name}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded border border-gray-200">
                        <h5 className="font-bold text-xs text-gray-500 uppercase mb-3">
                          Passport Details
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div>
                            <label className={labelStyle}>Passport No</label>
                            <input
                              type="text"
                              className={inputStyle}
                              value={p.passportNumber}
                              onChange={(e) =>
                                handleInputChange(
                                  idx,
                                  "passportNumber",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div>
                            <label className={labelStyle}>
                              Issuing Country
                            </label>
                            <select
                              className={inputStyle}
                              value={p.passportIssuingCountry}
                              onChange={(e) =>
                                handleInputChange(
                                  idx,
                                  "passportIssuingCountry",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">Select Country</option>
                              {countriesData.map((c: any) => (
                                <option key={c.code} value={c.name}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className={labelStyle}>Issue Date</label>
                            <input
                              type="date"
                              className={inputStyle}
                              value={p.passportIssueDate}
                              onChange={(e) =>
                                handleInputChange(
                                  idx,
                                  "passportIssueDate",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div>
                            <label className={labelStyle}>Expiry Date</label>
                            <input
                              type="date"
                              className={inputStyle}
                              value={p.passportExpiry}
                              onChange={(e) =>
                                handleInputChange(
                                  idx,
                                  "passportExpiry",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-blue-50 rounded border border-blue-100">
                        <h5 className="font-bold text-xs text-blue-600 uppercase mb-3 flex items-center">
                          <DollarSign size={14} className="mr-2" /> Fare Details
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={labelStyle}>Base Price</label>
                            <input
                              type="number"
                              className={inputStyle}
                              value={p.price}
                              onChange={(e) =>
                                handleInputChange(idx, "price", e.target.value)
                              }
                            />
                          </div>
                          <div>
                            <label className={labelStyle}>Tax</label>
                            <input
                              type="number"
                              className={inputStyle}
                              value={p.tax}
                              onChange={(e) =>
                                handleInputChange(idx, "tax", e.target.value)
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 mb-8 p-5 bg-orange-50 rounded-xl border border-orange-200">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-bold text-orange-800 flex items-center">
                <Luggage size={20} className="mr-2" /> Baggage Allowance
              </h4>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-600">Unit:</span>
                <div
                  onClick={() =>
                    setBaggageConfig({
                      ...baggageConfig,
                      isKg: !baggageConfig.isKg,
                    })
                  }
                  className={`cursor-pointer w-14 h-8 flex items-center rounded-full p-1 transition-colors ${
                    baggageConfig.isKg ? "bg-orange-500" : "bg-blue-500"
                  }`}
                >
                  <div
                    className={`bg-white w-6 h-6 rounded-full shadow transform duration-200 flex items-center justify-center text-[10px] font-bold ${
                      baggageConfig.isKg ? "translate-x-0" : "translate-x-6"
                    }`}
                  >
                    {baggageConfig.isKg ? "Kg" : "Pc"}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h5 className="text-xs font-bold text-gray-500 uppercase mb-2">
                  Outbound Flight
                </h5>
                <div className="grid grid-cols-3 gap-3">
                  {activeTypes.has("ADT") && (
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">
                        Adult
                      </label>
                      <input
                        type="number"
                        value={baggageConfig.outbound.ADT}
                        onChange={(e) =>
                          handleBaggageChange("outbound", "ADT", e.target.value)
                        }
                        className="w-full p-2 border rounded border-orange-200 focus:ring-orange-500"
                      />
                    </div>
                  )}
                  {activeTypes.has("CHD") && (
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">
                        Child
                      </label>
                      <input
                        type="number"
                        value={baggageConfig.outbound.CHD}
                        onChange={(e) =>
                          handleBaggageChange("outbound", "CHD", e.target.value)
                        }
                        className="w-full p-2 border rounded border-orange-200 focus:ring-orange-500"
                      />
                    </div>
                  )}
                  {activeTypes.has("INF") && (
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">
                        Infant
                      </label>
                      <input
                        type="number"
                        value={baggageConfig.outbound.INF}
                        onChange={(e) =>
                          handleBaggageChange("outbound", "INF", e.target.value)
                        }
                        className="w-full p-2 border rounded border-orange-200 focus:ring-orange-500"
                      />
                    </div>
                  )}
                </div>
              </div>

              {hasReturnFlight && (
                <div>
                  <h5 className="text-xs font-bold text-gray-500 uppercase mb-2">
                    Return Flight
                  </h5>
                  <div className="grid grid-cols-3 gap-3">
                    {activeTypes.has("ADT") && (
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">
                          Adult
                        </label>
                        <input
                          type="number"
                          value={baggageConfig.inbound.ADT}
                          onChange={(e) =>
                            handleBaggageChange(
                              "inbound",
                              "ADT",
                              e.target.value,
                            )
                          }
                          className="w-full p-2 border rounded border-blue-200 focus:ring-blue-500"
                        />
                      </div>
                    )}
                    {activeTypes.has("CHD") && (
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">
                          Child
                        </label>
                        <input
                          type="number"
                          value={baggageConfig.inbound.CHD}
                          onChange={(e) =>
                            handleBaggageChange(
                              "inbound",
                              "CHD",
                              e.target.value,
                            )
                          }
                          className="w-full p-2 border rounded border-blue-200 focus:ring-blue-500"
                        />
                      </div>
                    )}
                    {activeTypes.has("INF") && (
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">
                          Infant
                        </label>
                        <input
                          type="number"
                          value={baggageConfig.inbound.INF}
                          onChange={(e) =>
                            handleBaggageChange(
                              "inbound",
                              "INF",
                              e.target.value,
                            )
                          }
                          className="w-full p-2 border rounded border-blue-200 focus:ring-blue-500"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-col items-end mb-6 space-y-1">
              <p className="text-gray-500 text-sm">
                Base Fare: {currency} {totalFare.toFixed(2)}
              </p>
              <p className="text-gray-500 text-sm">
                Total Tax: {currency} {totalTax.toFixed(2)}
              </p>
              <div className="text-3xl font-bold text-gray-800">
                <span className="text-sm font-normal text-gray-500 mr-2">
                  Grand Total:
                </span>
                {currency} {grandTotal.toLocaleString()}
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <button
                onClick={() => setStep(isNDC ? 1.6 : 2)}
                className="px-6 py-3 text-gray-600 border rounded-lg hover:bg-gray-50 font-medium"
              >
                Back
              </button>
              <PrimaryButton
                onClick={handleSave}
                disabled={loading}
                className="px-8 py-3 w-full md:w-auto justify-center shadow-lg"
              >
                {loading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  "Confirm & Save Booking"
                )}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* --- STEP 4: SUCCESS --- */}
      {step === 4 && (
        <div className="bg-white p-10 shadow-xl rounded-xl border border-gray-200 text-center max-w-2xl mx-auto">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h3 className="text-3xl font-bold text-gray-800 mb-2">
            Booking Saved!
          </h3>
          <p className="text-gray-600 mb-8">
            The booking has been successfully imported into the system.
          </p>

          <div className="bg-gray-50 p-6 rounded-lg text-left border border-gray-200 mb-8">
            <div className="grid grid-cols-2 gap-y-4 gap-x-8">
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold">
                  PNR Number
                </p>
                <p className="text-lg font-mono">{pnr}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold">
                  Reference ID
                </p>
                <p className="text-lg font-mono text-blue-600">
                  {savedReference}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold">
                  Total Amount
                </p>
                <p className="text-lg font-bold">
                  {currency} {grandTotal.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold">
                  Status
                </p>
                <span
                  className={`inline-block px-2 py-1 rounded text-xs font-bold ${
                    bookingStatus === "Completed"
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {bookingStatus}
                </span>
              </div>
              {bookingStatus === "Completed" && (
                <>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold">
                      Payment Type
                    </p>
                    <p className="text-sm font-medium">{paymentType}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold">
                      Paid Date
                    </p>
                    <p className="text-sm font-medium">{paidAt}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          <PrimaryButton onClick={resetForm} className="px-8 py-3 w-full">
            Import Another Booking
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

export default TerminalBookingImport;
